import { z } from 'zod/v4';

export const ElementSchema = z.enum(['Fuoco', 'Acqua', 'Aria', 'Terra']);
export const SuitSchema = z.enum(['Bastoni', 'Coppe', 'Spade', 'Denari']);
export const ArcanaTypeSchema = z.enum(['major', 'minor']);
export const OrientationSchema = z.enum(['upright', 'reversed']);
export const PillarSchema = z.enum(['Severità', 'Equilibrio', 'Misericordia']);
export const ZodiacModalitySchema = z.enum(['Cardinale', 'Fisso', 'Mutabile']);
export const QabalisticWorldSchema = z.enum(['Atziluth', 'Briah', 'Yetzirah', 'Assiah']);
export const CourtRankSchema = z.enum(['Re', 'Regina', 'Cavaliere', 'Fante']);
export const ReadingModeSchema = z.enum(['standard', 'lilith', 'qliphotic', 'kabbalistic']);
export const SpreadCategorySchema = z.enum(['core', 'special', 'lilith', 'kabbalistic', 'custom']);
export const DifficultyLevelSchema = z.enum([
  'beginner',
  'intermediate',
  'advanced',
  'initiatic',
]);
export const DignitySchema = z.enum(['essential', 'accidental', 'neutral']);

export const SephirahNameSchema = z.enum([
  'Kether',
  'Chokmah',
  'Binah',
  'Chesed',
  'Geburah',
  'Tiphareth',
  'Netzach',
  'Hod',
  'Yesod',
  'Malkuth',
]);

export const QliphahNameSchema = z.enum([
  'Thaumiel',
  'Ghagiel',
  'Satariel',
  'Gha_agsheblah',
  'Golachab',
  'Thagirion',
  'A_arab_Zaraq',
  'Samael',
  'Gamaliel',
  'Nahemoth',
]);

export const ClassicalPlanetSchema = z.enum([
  'Saturno',
  'Giove',
  'Marte',
  'Sole',
  'Venere',
  'Mercurio',
  'Luna',
]);

export const ZodiacSignSchema = z.enum([
  'Ariete',
  'Toro',
  'Gemelli',
  'Cancro',
  'Leone',
  'Vergine',
  'Bilancia',
  'Scorpione',
  'Sagittario',
  'Capricorno',
  'Acquario',
  'Pesci',
]);

export const HebrewLetterSchema = z.enum([
  'Aleph',
  'Beth',
  'Gimel',
  'Daleth',
  'Heh',
  'Vav',
  'Zayin',
  'Cheth',
  'Teth',
  'Yod',
  'Kaph',
  'Lamed',
  'Mem',
  'Nun',
  'Samekh',
  'Ayin',
  'Peh',
  'Tzaddi',
  'Qoph',
  'Resh',
  'Shin',
  'Tav',
]);

export const ColorScalesSchema = z.object({
  king: z.string().min(1),
  queen: z.string().min(1),
  prince: z.string().min(1),
  princess: z.string().min(1),
});

export const SephirahSchema = z.object({
  number: z.number().int().positive(),
  name: SephirahNameSchema,
  hebrew: z.string().min(1),
  translation: z.string().min(1),
  pillar: PillarSchema,
  planet: z.union([ClassicalPlanetSchema, z.literal('Zodiac'), z.literal('Primum_Mobile')]),
  archangel: z.string().min(1),
  angelic_order: z.string().min(1),
  divine_name: z.string().min(1),
  color_scales: ColorScalesSchema,
  virtue: z.string().min(1),
  vice: z.string().min(1),
  body_part: z.string().min(1),
  incense: z.string().min(1),
  gemstone: z.string().min(1),
  tarot_number: z.number().int().nonnegative(),
  description: z.string().min(1),
});

export const QabalisticPathSchema = z.object({
  number: z.number().int().positive(),
  hebrew_letter: HebrewLetterSchema,
  hebrew_char: z.string().min(1),
  letter_meaning: z.string().min(1),
  letter_value: z.number().int().positive(),
  from_sephirah: SephirahNameSchema,
  to_sephirah: SephirahNameSchema,
  major_arcana_id: z.number().int().nonnegative(),
  astro_attribution: z.string().min(1),
  color_scales: ColorScalesSchema,
  description: z.string().min(1),
});

export const QliphahSchema = z.object({
  number: z.number().int().positive(),
  name: QliphahNameSchema,
  hebrew: z.string().min(1),
  translation: z.string().min(1),
  opposing_sephirah: SephirahNameSchema,
  ruling_demon: z.string().min(1),
  secondary_entity: z.string().min(1).nullable(),
  psychological_meaning: z.string().min(1),
  initiatic_challenge: z.string().min(1),
  specchio_di_lilith_position: z.number().int().positive().nullable(),
  sigil_description: z.string().min(1),
  color: z.string().min(1),
});

export const TunnelOfSetSchema = z.object({
  number: z.number().int().positive(),
  name: z.string().min(1),
  corresponding_path: z.number().int().positive(),
  from_qliphah: QliphahNameSchema,
  to_qliphah: QliphahNameSchema,
  hebrew_letter_shadow: HebrewLetterSchema,
  description: z.string().min(1),
  tarot_shadow_meaning: z.string().min(1),
});

export const DecanDatesSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

export const ShemAngelSchema = z.object({
  name: z.string().min(1),
  hebrew: z.string().min(1),
  psalm_verse: z.string().min(1),
});

export const DecanSchema = z.object({
  number: z.number().int().positive(),
  zodiac_sign: ZodiacSignSchema,
  decan_position: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  degrees: z.string().min(1),
  chaldean_ruler: ClassicalPlanetSchema,
  dates: DecanDatesSchema,
  tarot_card_id: z.number().int().nonnegative(),
  hermetic_title: z.string().min(1),
  angels_shemhamphorash: z.object({
    day: ShemAngelSchema,
    night: ShemAngelSchema,
  }),
  goetia_demon: z
    .object({
      name: z.string().min(1),
      rank: z.string().min(1),
      powers: z.string().min(1),
    })
    .nullable(),
  picatrix_image: z.string().min(1),
  body_region: z.string().min(1),
  geomantic_figure: z.string().min(1).nullable(),
});

export const TarotCardSchema = z.object({
  id: z.number().int().nonnegative(),
  name: z.string().min(1),
  slug: z.string().min(1),
  arcana: ArcanaTypeSchema,
  hermetic_title: z.string().min(1),
  suit: SuitSchema.nullable(),
  rank: CourtRankSchema.nullable(),
  number: z.number().int().nonnegative(),
  court: z.boolean(),
  element: ElementSchema,
  element_secondary: ElementSchema.nullable(),
  qabalistic_world: QabalisticWorldSchema.nullable(),
  sephirah: SephirahNameSchema.nullable(),
  path_number: z.number().int().positive().nullable(),
  hebrew_letter: HebrewLetterSchema.nullable(),
  hebrew_char: z.string().min(1).nullable(),
  tree_position: z.string().min(1).nullable(),
  qliphah: QliphahNameSchema.nullable(),
  tunnel_of_set: z.number().int().positive().nullable(),
  qliphotic_meaning: z.string().min(1),
  astro_primary: z.string().min(1),
  zodiac_sign: ZodiacSignSchema.nullable(),
  zodiac_modality: ZodiacModalitySchema.nullable(),
  decan: DecanSchema.nullable(),
  chaldean_mirror_card_id: z.number().int().nonnegative().nullable(),
  angels_day: z.string().min(1).nullable(),
  angels_night: z.string().min(1).nullable(),
  goetia_shadow: z.string().min(1).nullable(),
  gemstone: z.string().min(1).nullable(),
  incense: z.string().min(1).nullable(),
  color_primary: z.string().min(1),
  body_part: z.string().min(1).nullable(),
  musical_note: z.string().min(1).nullable(),
  keywords_up: z.array(z.string().min(1)),
  keywords_rev: z.array(z.string().min(1)),
  meaning_up: z.string().min(1),
  meaning_rev: z.string().min(1),
  meaning_love: z.string().min(1),
  meaning_work: z.string().min(1),
  meaning_spirit: z.string().min(1),
  meaning_psyche: z.string().min(1),
  lilith_voice: z.string().min(1),
  lilith_qliphah_link: QliphahNameSchema.nullable(),
  simbologia: z.string().min(1),
  symbol_emoji: z.string().min(1),
  numerology_value: z.number().int().nonnegative(),
  numerology_meaning: z.string().min(1),
  dignity: DignitySchema.nullable(),
  solar_dates: DecanDatesSchema.nullable(),
});

export const TarotCardJsonSchema = z.toJSONSchema(TarotCardSchema);
